import { useEffect, useState } from 'react';
import { findLanguage } from '@tolk/shared';
import { leesKamers, sluitKamer, verslagPad, type KamerInLijst } from '../lib/api';
import { onthoudMijnKamer } from '../lib/mijnKamers';
import { verlooptTekst } from '../lib/verlooptijd';

/**
 * De gesprekken die nu nog lopen, met een weg terug naar binnen.
 *
 * WAAROM DIT ER IS. Tot 13 september 2026 kon je een gesprek aanmaken en de
 * link daarna nergens meer terugvinden. Was je het tabblad kwijt, dan was de
 * kamer praktisch weg - hij leefde nog op de server, maar er was geen scherm
 * dat hem noemde. De route bestond al vanaf het begin; alleen het scherm
 * ontbrak.
 *
 * ALLEEN VOOR DE BEHEERDER, en Nederlands zoals de rest van het beheer. De
 * lijst komt van achter de inlog; een gast krijgt hem nooit te zien.
 *
 * Geen kamers of de server geeft niets? Dan verdwijnt dit blok. Een lege lijst
 * met de mededeling dat hij leeg is, kost ruimte boven het formulier dat op dat
 * moment het enige antwoord is.
 */

/** De twee talen als vlaggen met hun eigen naam. Onbekend blijft leeg. */
function talen(kamer: KamerInLijst): string {
  const a = findLanguage(kamer.langA);
  const b = findLanguage(kamer.langB);
  return `${a?.flag ?? '🏳️'} ${a?.label ?? kamer.langA} → ${b?.flag ?? '🏳️'} ${b?.label ?? kamer.langB}`;
}

/**
 * Een prullenbak, als tekening en niet als emoji.
 *
 * Een emoji wordt op elk toestel anders getekend en neemt de kleur niet over
 * van de knop eromheen - en juist de kleur draagt hier de betekenis, want rood
 * is in dit project alleen voor wat je niet terug kunt draaien.
 */
function Prullenbak() {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="w-3.5 h-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <path d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L12 4M6.5 7v4M9.5 7v4" />
    </svg>
  );
}

function Rij({ kamer, opGesloten }: { kamer: KamerInLijst; opGesloten: (id: string) => void }) {
  const [gekopieerd, setGekopieerd] = useState(false);
  const [vraagtBevestiging, setVraagtBevestiging] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  async function kopieer(): Promise<void> {
    try {
      await navigator.clipboard.writeText(kamer.link);
      setGekopieerd(true);
      window.setTimeout(() => setGekopieerd(false), 2000);
    } catch {
      // Geen toegang tot het klembord (geen https, of geweigerd). De knop "Ga
      // erheen" werkt nog gewoon, dus je komt er hoe dan ook.
    }
  }

  /**
   * HIER WORDT HET BRIEFJE GEZET. Zonder dit zie je de kostenbalk niet in een
   * kamer die je op een ANDER toestel aanmaakte - je komt netjes binnen, maar
   * de bedragen blijven weg zonder dat iets uitlegt waarom. Veilig, want deze
   * lijst staat achter de inlog: wie hem ziet, is de beheerder.
   */
  function gaNaarBinnen(): void {
    onthoudMijnKamer(kamer.id);
    window.location.href = `/r/${kamer.id}`;
  }

  async function sluiten(): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      await sluitKamer(kamer.id);
      opGesloten(kamer.id);
    } catch {
      // De rij blijft staan. Een gesprek dat nog leeft laten verdwijnen omdat
      // het sluiten mislukte, is de ergste van de twee fouten: dan denk je dat
      // hij dicht is terwijl de link het gewoon nog doet.
      setFout('Sluiten lukte niet. Probeer het nog een keer.');
      setBezig(false);
      setVraagtBevestiging(false);
    }
  }

  /*
    DE BEVESTIGING STAAT IN DE RIJ ZELF en niet in een venster van de browser.
    Een window.confirm ziet er op elk toestel anders uit en zegt niet WELK
    gesprek je op het punt staat te sluiten. Hier staat de naam er gewoon in.
  */
  if (vraagtBevestiging) {
    return (
      <li className="border-2 border-gio-danger/40 bg-gio-danger/5 rounded-xl p-2.5 space-y-2">
        <p className="text-[11px] text-slate-650 leading-relaxed">
          <b className="text-gio-dark">{kamer.label === '' ? 'Dit gesprek' : kamer.label}</b>{' '}
          sluiten? De link werkt daarna niet meer, ook niet voor je gesprekspartner. Dit kan
          niet ongedaan gemaakt worden.
        </p>

        {/*
          DE LAATSTE KANS OP HET VERSLAG VANUIT DEZE LIJST. Na het sluiten staat
          het gesprek er niet meer in, en wordt het een uur later gewist. Het
          verslag zelf werkt in dat uur nog, maar geen scherm wijst er dan meer
          naar.
        */}
        <p className="text-[11px] text-slate-650">
          Wil je het gesprek bewaren?{' '}
          <a
            href={verslagPad(kamer.id)}
            target="_blank"
            rel="noopener"
            className="font-bold text-gio-blue hover:text-gio-dark underline"
          >
            Open eerst het gespreksverslag
          </a>
          .
        </p>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void sluiten()}
            disabled={bezig}
            className="flex-1 py-1.5 rounded-lg bg-gio-danger text-white text-[11px] font-bold
                       transition-opacity duration-155 disabled:opacity-40"
          >
            {bezig ? 'Bezig…' : 'Ja, sluiten'}
          </button>
          <button
            type="button"
            onClick={() => setVraagtBevestiging(false)}
            disabled={bezig}
            className="flex-1 py-1.5 rounded-lg border-2 border-slate-250 bg-white text-slate-650
                       text-[11px] font-bold hover:border-gio-blue hover:text-gio-blue
                       transition-colors duration-155 disabled:opacity-40"
          >
            Nee, laat staan
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="border-2 border-slate-250 rounded-xl p-2.5 space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-bold text-xs text-gio-dark truncate min-w-0">
          {kamer.label === '' ? 'Zonder naam' : kamer.label}
        </span>
        {kamer.pinVereist && (
          <span className="shrink-0 text-[10px] text-slate-450 whitespace-nowrap">met pincode</span>
        )}
      </div>

      <p className="text-[11px] text-slate-550">{talen(kamer)}</p>

      <p className="text-[11px] text-slate-450">
        verloopt <span className="whitespace-nowrap">{verlooptTekst(kamer.expiresAt)}</span>
        {' · '}
        <a
          href={verslagPad(kamer.id)}
          target="_blank"
          rel="noopener"
          className="font-bold text-gio-blue hover:text-gio-dark"
        >
          gespreksverslag
        </a>
      </p>

      {fout !== null && <p className="text-[11px] text-gio-danger">{fout}</p>}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={gaNaarBinnen}
          className="flex-1 py-1.5 rounded-lg bg-gio-blue hover:bg-gio-dark text-white
                     text-[11px] font-bold transition-colors duration-155"
        >
          Ga erheen
        </button>
        <button
          type="button"
          onClick={() => void kopieer()}
          className="flex-1 py-1.5 rounded-lg border-2 border-slate-250 text-slate-650
                     text-[11px] font-bold hover:border-gio-blue hover:text-gio-blue
                     transition-colors duration-155"
        >
          {gekopieerd ? 'Gekopieerd' : 'Kopieer de link'}
        </button>

        {/*
          De prullenbak staat APART en is grijs tot je hem aanraakt. Hij hoort
          niet even hard te roepen als de twee knoppen die je elke keer gebruikt;
          rood wordt hij pas als je erop staat.
        */}
        <button
          type="button"
          onClick={() => setVraagtBevestiging(true)}
          aria-label={`Sluit ${kamer.label === '' ? 'dit gesprek' : kamer.label}`}
          title="Dit gesprek sluiten"
          className="shrink-0 w-8 rounded-lg border-2 border-slate-250 text-slate-400
                     hover:text-gio-danger hover:border-gio-danger/40 hover:bg-gio-danger/10
                     transition-colors duration-155 flex items-center justify-center"
        >
          <Prullenbak />
        </button>
      </div>
    </li>
  );
}

export default function Kamerlijst({ verversSleutel }: { verversSleutel: number }) {
  const [kamers, setKamers] = useState<KamerInLijst[]>([]);

  /**
   * INGEKLAPT IS DE STAND VAN RUST, net als bij de woordenlijst en de
   * instellingen. Het AANTAL staat wel in de kop: zonder dat getal weet je niet
   * of het de moeite is om te kijken, en dan klap je hem elke keer maar open.
   */
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let levend = true;
    void leesKamers()
      .then(({ kamers: nieuw }) => {
        if (levend) setKamers(nieuw);
      })
      .catch(() => {
        // Poort dicht, netwerk weg, sessie verlopen. Alle drie hetzelfde
        // antwoord: geen lijst. Het formulier eronder werkt gewoon, en een
        // foutmelding boven een scherm dat het doet, helpt niemand.
        if (levend) setKamers([]);
      });
    return () => {
      levend = false;
    };
    // verversSleutel loopt op zodra er een kamer bij komt. Geen klok: zolang er
    // niets gebeurt, valt er niets te verversen.
  }, [verversSleutel]);

  if (kamers.length === 0) return null;

  return (
    <section aria-label="Gesprekken die nog lopen">
      {/*
        Een knop en geen <details>, want de woordenlijst klapt ook zo open. Twee
        manieren om hetzelfde te doen is er een te veel.
      */}
      <button
        type="button"
        onClick={() => setOpen((aan) => !aan)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide
                   text-slate-450 hover:text-slate-650 cursor-pointer select-none
                   transition-colors duration-155"
      >
        <span>Gesprekken die nog lopen</span>
        <span className="font-mono text-slate-350">{kamers.length}</span>
        <span className="font-mono">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <ul className="space-y-2 mt-2 animate-fade-in">
          {kamers.map((kamer) => (
            <Rij
              key={kamer.id}
              kamer={kamer}
              opGesloten={(id) => setKamers((lijst) => lijst.filter((k) => k.id !== id))}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
